import { useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import SectionHeader from "../../ui-kit/section-header";
import Toggle from "../../ui-kit/toggle";
import { CustomColor } from "./appearance";
import ColorPickerModal from "./color-picker";
import CustomColorRow from "./custom-color";

type CustomThemesSectionProps = Readonly<{
  enabled: boolean;
  onToggle: (value: boolean) => void;
  colors: CustomColor[];
  onColorChange: (id: string, hex: string) => void;
}>;

const CustomThemesSection = ({
  enabled,
  onToggle,
  colors,
  onColorChange,
}: CustomThemesSectionProps) => {
  const { t } = useTranslation();

  // Track which color item is being edited — null means modal is closed
  const [editingItem, setEditingItem] = useState<CustomColor | null>(null);

  const handleApply = (hex: string) => {
    if (editingItem) {
      onColorChange(editingItem.id, hex);
    }
    setEditingItem(null);
  };

  return (
    <View className="gap-4">
      <View className="flex-row justify-between items-start">
        <View className="flex-1 pr-4">
          <SectionHeader
            title={t("LABEL_CUSTOM_THEMES")}
            subtitle={t("LABEL_CUSTOM_THEMES_DESC")}
          />
        </View>
        <Toggle value={enabled} onChange={onToggle} />
      </View>

      {enabled && (
        <View className="flex-col gap-3">
          {colors.map((item) => (
            <CustomColorRow
              key={item.id}
              item={item}
              onEdit={() => setEditingItem(item)}
            />
          ))}
        </View>
      )}

      {/* Single shared modal — opens with the correct item's hex */}
      <ColorPickerModal
        visible={editingItem !== null}
        initialColor={editingItem?.hex ?? "#ff0000"}
        onClose={() => setEditingItem(null)}
        onApply={handleApply}
      />
    </View>
  );
};

export default CustomThemesSection;
